// FormSheet (page patterns, BL-189): a few fields to fill in beside the page (Invite someone, On your
// paperwork, How to get in), on the panel layer: docked at the right, the window's height, the page
// still readable under the veil (DESIGN.md §1). The first line is its title beside Close; then the
// fields; then, in view however long the form, the foot: the action first (filled), Cancel after it.
// Saving keeps the words and draws the button dashed (waiting); a save that fails says so in red and
// keeps every change; closing with changes not saved asks first, in the foot, never a second layer.
// The focus goes in when it opens and back to what opened it when it closes; Escape is Cancel.
// Use it where the form belongs beside what it changes (the page stays in view); for one decision, or
// a form that must be finished before anything else, use ConfirmDialog or FormDialog. Its save rules
// are FormDialog's. Several at once (the price book's Edit them together, DEC-019): every field starts
// at "Keep as it is" (KEEP), and the action counts what it changes ("Change 3 items"), off until
// something does. Read only (`isReadOnly`: who can't change it here), the foot has Close alone and the
// note says why. Reads only --formsheet-*; Layer, Button and Field read their own.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '../../components/Button/Button.tsx';
import { Layer } from '../../components/Layer/Layer.tsx';
import { plural } from '../../format.ts';
import './FormSheet.css';

/** Editing several at once: each SelectField's first option, which changes nothing. */
export const KEEP = { value: '', label: 'Keep as it is' } as const;

export interface FormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Its first line, and its name ("Invite someone"). */
  title: string;
  /** One muted line under the title (what it changes, who sees it). */
  line?: ReactNode;
  /** The fields: TextField, SelectField, Checkbox, ChoiceGroup. */
  children: ReactNode;
  /** "Send the invitation", "Save changes". */
  submitLabel?: string;
  cancelLabel?: string;
  /** Whether anything differs from what was opened: closing then asks first. */
  isDirty: boolean;
  /** Checked before saving: false when a field is wrong (the fields say why; focus goes to the first). */
  validate?: () => boolean;
  /** Saves; throw to say it didn't save (the words stay). Closes on success. */
  onSubmit: () => Promise<void> | void;
  failText?: string;
  /** One sentence above the buttons: what Save does, or why it's off. */
  note?: ReactNode;
  isSubmitDisabled?: boolean;
  /** Storybook only: draws it failed, or asking about changes not saved. */
  previewState?: 'failed' | 'discard';
  /** Several at once: how many, what they are ("item"), and how many fields change. The action says
   * "Change 3 items" and is off until a field changes; the note says what will change. */
  bulk?: { count: number; noun: string; changes: number };
  /** Nothing here can be changed: no action, Close alone, the note saying why. */
  isReadOnly?: boolean;
}

export function FormSheet({
  isOpen,
  onClose,
  title,
  line,
  children,
  submitLabel: asked = 'Save changes',
  cancelLabel = 'Cancel',
  isDirty,
  validate,
  onSubmit,
  failText = 'Your changes didn’t save. They’re still here; try again.',
  note: given,
  isSubmitDisabled: off,
  previewState,
  bulk,
  isReadOnly,
}: FormSheetProps) {
  const them = bulk ? plural(bulk.count, bulk.noun) : '';
  const submitLabel = bulk ? `Change ${them}` : asked;
  const isSubmitDisabled = off || bulk?.changes === 0;
  const note =
    given ??
    (bulk
      ? bulk.changes === 0
        ? `Nothing changes until you pick something other than “${KEEP.label}”.`
        : `${plural(bulk.changes, 'change')} to ${them}.`
      : undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(previewState === 'failed' ? failText : null);
  const [asking, setAsking] = useState(previewState === 'discard');
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(previewState === 'failed' ? failText : null);
    setAsking(previewState === 'discard');
  }, [isOpen]);

  // Close, Escape or Cancel: with changes it asks first; while saving, nothing closes it
  const close = () => {
    if (busy) return;
    if (isDirty && !asking) return setAsking(true);
    onClose();
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || isSubmitDisabled || isReadOnly) return;
    setAsking(false);
    if (validate && !validate()) {
      requestAnimationFrame(() => form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit();
      setBusy(false);
      onClose();
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error && err.message ? err.message : failText);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fl-formsheet">
      <form ref={form} className="fl-formsheet__form" onSubmit={(e) => void submit(e)} noValidate>
        <Layer level="panel" label={title} onClose={close} width="wide" takesFocus>
          <div className="fl-formsheet__in">
            <h2 className="fl-formsheet__title">{title}</h2>
            {line ? <p className="fl-formsheet__line">{line}</p> : null}
            <div className="fl-formsheet__body">{children}</div>
            <div className="fl-formsheet__foot">
              {error ? (
                <p className="fl-formsheet__say" data-bad="" role="alert">
                  {error}
                </p>
              ) : null}
              {asking ? (
                <>
                  <p className="fl-formsheet__say" role="alert">
                    Discard your changes?
                  </p>
                  <span className="fl-formsheet__acts">
                    <Button variant="primary" onPress={onClose}>
                      Discard them
                    </Button>
                    <Button onPress={() => setAsking(false)}>Keep editing</Button>
                  </span>
                </>
              ) : (
                <>
                  {note ? (
                    <p className="fl-formsheet__say" aria-live="polite">
                      {note}
                    </p>
                  ) : null}
                  <span className="fl-formsheet__acts">
                    {isReadOnly ? (
                      <Button onPress={onClose}>Close</Button>
                    ) : (
                      <>
                        <Button
                          variant="primary"
                          type="submit"
                          isBusy={busy}
                          isDisabled={!busy && isSubmitDisabled}
                        >
                          {submitLabel}
                        </Button>
                        <Button onPress={close} isDisabled={busy}>
                          {cancelLabel}
                        </Button>
                      </>
                    )}
                  </span>
                </>
              )}
            </div>
          </div>
        </Layer>
      </form>
    </div>
  );
}
